import { chains, type Group, type Position } from './position.js';
import { EMPTY, opponent, type Color, type Vertex } from './types.js';
import { hashKey } from './zobrist.js';

/**
 * Life and death — whether a group can be killed.
 *
 * This is the one part of Go a beginner cannot infer from the rules, and until
 * now the engine did not attempt it: scoring assumed every stone on the board
 * was alive, which is right for a finished game and wrong for a position with a
 * dead group still sitting on it.
 *
 * Two layers, kept apart on purpose:
 *
 *   1. `passAliveChains` — Benson's algorithm. This is a *proof*: these groups
 *      cannot be captured even if their owner never answers another move, no
 *      matter how the opponent plays.
 *   2. `readGroup` — an exhaustive search of a surrounded group's eye space,
 *      settled by layer 1's condition applied to the group in front of it. Exact
 *      within the space it can see, and it derives the classical dead shapes from
 *      the rules rather than listing them.
 *
 * Nothing here guesses. A group whose eye space is too large to search, or whose
 * fate turns on a capture elsewhere on the board, comes back `unknown` — telling
 * a learner a group is dead when we cannot show why would teach the wrong
 * lesson, and it is the kind of mistake they would take on faith.
 */

/**
 * Largest eye space the reader will attempt. Every shape in the primers fits in
 * six points, and a single eye space bigger than this is alive in any case.
 */
const MAX_EYE_SPACE = 6;

/**
 * Ceilings on positions examined, in the same spirit as the ladder reader's depth
 * limit and MCTS's wall clock: the coach runs on every move of a live game, so a
 * hard shape must cost accuracy, never a stalled board.
 *
 * Two of them, because one is not enough. A single read that resolves has never
 * been seen to need more than about a thousand positions, so `NODE_BUDGET` is
 * generous for real work and only bites on shapes that were not going to resolve
 * anyway. But a filled 19x19 board holds dozens of enclosed groups, and dozens of
 * truncated reads still add up to a visible stall — hence a second ceiling across
 * the whole board.
 *
 * Running out can only turn an answer into `unknown`. It can never produce a
 * wrong one, which is what makes it safe to tune for speed.
 */
const NODE_BUDGET = 2_000;
const POSITION_BUDGET = 8_000;

/** Shared node allowance for every read taken of one position. */
export interface ReadBudget {
  nodes: number;
}

/**
 * The board-wide allowance for one position. It shrinks as the board grows,
 * because examining a position costs time proportional to the board: the ceiling
 * that matters is wall-clock, and a learner on 19x19 should not wait longer for
 * the coach than one on 9x9. `POSITION_BUDGET` is quoted for a 9x9 board.
 */
export function readBudget(position: Position, nodes?: number): ReadBudget {
  if (nodes !== undefined) return { nodes };
  return { nodes: Math.max(1_000, Math.round((POSITION_BUDGET * 81) / position.area)) };
}

export type LifeStatus =
  /** Cannot be killed. */
  | 'alive'
  /** Cannot be saved, even moving first. */
  | 'dead'
  /** Whoever plays the vital point decides it. */
  | 'unsettled'
  /** Not resolvable from the position alone — usually still room to run. */
  | 'unknown';

export type LifeCertainty =
  /** Benson's algorithm proved it. */
  | 'proven'
  /** An exhaustive search of the eye space settled it. */
  | 'read'
  /** No conclusion was reached. */
  | 'unread';

export interface GroupLife {
  readonly color: Color;
  readonly stones: readonly Vertex[];
  readonly status: LifeStatus;
  readonly certainty: LifeCertainty;
  /** Every empty point the group can still play in. */
  readonly eyeSpace: readonly Vertex[];
  /** Eye-shaped points that really are eyes. */
  readonly eyes: readonly Vertex[];
  /** Points that look like eyes but cannot be relied on — the diagonals are lost. */
  readonly falseEyes: readonly Vertex[];
  /** Present when `status` is `unsettled`: the move that settles it, for either side. */
  readonly vitalPoint?: Vertex;
}

// --- Layer 1: unconditional life ------------------------------------------

/**
 * Benson's algorithm for unconditional life.
 *
 * A chain is *pass-alive* when its owner can pass forever and still not lose it.
 * The condition is mutual: a chain needs two "vital" regions — enclosed empty
 * spaces every one of whose points touches that chain — and a region only
 * counts while every chain around it is still a candidate. So candidates are
 * struck off in rounds until the set stops shrinking, and what remains is
 * provably alive.
 *
 * Being a proof, it is strict: many groups a player would call alive are not
 * pass-alive, because they stay alive only by answering. `readGroup` is what
 * settles those.
 */
export function passAliveChains(position: Position, color: Color): Group[] {
  const candidates = chains(position, color);
  if (candidates.length === 0) return [];

  const chainAt = new Int32Array(position.area).fill(-1);
  candidates.forEach((chain, index) => {
    for (const stone of chain.stones) chainAt[stone] = index;
  });

  const regions = smallEnclosedRegions(position, color, chainAt);
  const chainLives = new Uint8Array(candidates.length).fill(1);
  const regionLives = new Uint8Array(regions.length).fill(1);

  for (let changed = true; changed; ) {
    changed = false;

    // A region only counts while every chain surrounding it is still a candidate.
    for (let r = 0; r < regions.length; r++) {
      if (!regionLives[r]) continue;
      for (const chain of regions[r].border) {
        if (!chainLives[chain]) {
          regionLives[r] = 0;
          changed = true;
          break;
        }
      }
    }

    // A chain only survives while two surviving regions are vital to it.
    for (let c = 0; c < candidates.length; c++) {
      if (!chainLives[c]) continue;
      let vital = 0;
      for (let r = 0; r < regions.length; r++) {
        if (regionLives[r] && regions[r].vitalTo.has(c)) vital++;
      }
      if (vital < 2) {
        chainLives[c] = 0;
        changed = true;
      }
    }
  }

  return candidates.filter((_, index) => chainLives[index] === 1);
}

/** True if the stone at `vertex` belongs to a provably unkillable group. */
export function isPassAlive(position: Position, vertex: Vertex): boolean {
  const color = position.at(vertex);
  if (color === EMPTY) return false;
  return passAliveChains(position, color).some((chain) => chain.stones.includes(vertex));
}

interface EnclosedRegion {
  readonly empties: Vertex[];
  /** Indices of the `color` chains bordering this region. */
  readonly border: Set<number>;
  /** Indices of the chains every empty point of this region touches. */
  readonly vitalTo: Set<number>;
}

/**
 * Connected regions of everything that is *not* `color` — empty points and enemy
 * stones together. Each one is bounded entirely by `color` stones by
 * construction, so the only question is whether it is *small*: a region with an
 * empty point that touches no `color` stone is open board, not an eye space.
 */
function smallEnclosedRegions(
  position: Position,
  color: Color,
  chainAt: Int32Array,
): EnclosedRegion[] {
  const { area, nbr, off } = position.geo;
  const seen = new Uint8Array(area);
  const found: EnclosedRegion[] = [];

  for (let start = 0; start < area; start++) {
    if (seen[start] || position.stones[start] === color) continue;

    const points: Vertex[] = [start];
    seen[start] = 1;
    const empties: Vertex[] = [];
    const border = new Set<number>();
    let small = true;

    for (let i = 0; i < points.length; i++) {
      const cur = points[i];
      const isEmpty = position.stones[cur] === EMPTY;
      let touchesColor = false;

      for (let k = off[cur]; k < off[cur + 1]; k++) {
        const n = nbr[k];
        if (position.stones[n] === color) {
          border.add(chainAt[n]);
          touchesColor = true;
        } else if (!seen[n]) {
          seen[n] = 1;
          points.push(n);
        }
      }

      if (isEmpty) {
        empties.push(cur);
        if (!touchesColor) small = false;
      }
    }

    // A region with no empty points cannot occur in a legal position — the enemy
    // stones filling it would have no liberties — so it is skipped rather than
    // counted as vacuously vital.
    if (!small || empties.length === 0) continue;
    found.push({ empties, border, vitalTo: vitalChains(position, empties, chainAt, border) });
  }
  return found;
}

/** The chains that every empty point of the region touches. */
function vitalChains(
  position: Position,
  empties: readonly Vertex[],
  chainAt: Int32Array,
  border: ReadonlySet<number>,
): Set<number> {
  const { nbr, off } = position.geo;
  const vital = new Set(border);

  for (const empty of empties) {
    if (vital.size === 0) break;
    const touching = new Set<number>();
    for (let k = off[empty]; k < off[empty + 1]; k++) {
      const chain = chainAt[nbr[k]];
      if (chain >= 0) touching.add(chain);
    }
    for (const chain of [...vital]) {
      if (!touching.has(chain)) vital.delete(chain);
    }
  }
  return vital;
}

// --- Layer 2: eye space and the read --------------------------------------

export interface EyeSpace {
  /** Empty points the group can still play in. */
  readonly empties: readonly Vertex[];
  /** Enemy stones trapped inside that space. */
  readonly enemies: readonly Vertex[];
  /**
   * False when the group can still reach open board, which makes its life a
   * whole-board question rather than a local one.
   */
  readonly confined: boolean;
}

/**
 * The space a group has to live in: every empty point reachable from its
 * liberties without crossing a stone, plus any enemy stones trapped inside.
 *
 * A group that can reach more than `limit` empty points is not confined, and no
 * amount of local reading will settle it.
 */
export function eyeSpace(position: Position, chain: Group, limit = MAX_EYE_SPACE): EyeSpace {
  const { area, nbr, off } = position.geo;
  const foe = opponent(chain.color);

  const inSpace = new Uint8Array(area);
  const empties: Vertex[] = [];
  for (const liberty of chain.liberties) {
    if (inSpace[liberty]) continue;
    inSpace[liberty] = 1;
    empties.push(liberty);
  }

  for (let i = 0; i < empties.length; i++) {
    if (empties.length > limit) return { empties, enemies: [], confined: false };
    const cur = empties[i];
    for (let k = off[cur]; k < off[cur + 1]; k++) {
      const n = nbr[k];
      if (position.stones[n] !== EMPTY || inSpace[n]) continue;
      inSpace[n] = 1;
      empties.push(n);
    }
  }

  // An enemy chain belongs to the eye space only if it is trapped in it. If any
  // of its liberties lie outside, it is part of the wall doing the surrounding.
  const enemies: Vertex[] = [];
  const checked = new Uint8Array(area);
  for (const empty of empties) {
    for (let k = off[empty]; k < off[empty + 1]; k++) {
      const n = nbr[k];
      if (position.stones[n] !== foe || checked[n]) continue;
      const enemy = position.group(n);
      if (!enemy) continue;
      for (const stone of enemy.stones) checked[stone] = 1;
      if (enemy.liberties.every((liberty) => inSpace[liberty] === 1)) {
        enemies.push(...enemy.stones);
      }
    }
  }

  return { empties, enemies, confined: empties.length > 0 && empties.length <= limit };
}

/** Eye-shaped points around a chain that really are eyes. */
export function trueEyes(position: Position, chain: Group): Vertex[] {
  return chain.liberties.filter((v) => position.isTrueEye(v, chain.color));
}

/**
 * Points that look like eyes but are not: the surrounding stones are cut apart
 * on the diagonals, so the opponent can take the point after all. A group with
 * two false eyes and nothing else is dead, which is why beginners lose groups
 * they were sure were safe.
 */
export function falseEyes(position: Position, chain: Group): Vertex[] {
  return chain.liberties.filter(
    (v) => position.isEyeLike(v, chain.color) && !position.isTrueEye(v, chain.color),
  );
}

/** `true` = the group lives, `false` = it dies, `null` = the search could not tell. */
type Verdict = boolean | null;

interface Search {
  /** The points play is confined to. */
  readonly mask: Uint8Array;
  /** Size of the original eye space, so a break-out can be detected. */
  readonly limit: number;
  /**
   * Transposition table. A resolved verdict is a proof and can always be reused;
   * an unresolved one only tells us that *this much* depth was not enough, so it
   * is reused only against a search with no more depth to spend. Getting this
   * wrong is not a wrong answer but an exponential one — with nothing cached,
   * the same positions are re-explored down every order of the same moves.
   */
  readonly memo: Map<string, { verdict: Verdict; depth: number }>;
  /** This group's allowance. */
  own: number;
  /** The allowance shared with every other group read from the same position. */
  readonly shared: ReadBudget;
}

/**
 * Reads a confined group by playing the fight out inside its own eye space.
 *
 * Both sides are searched exhaustively, and having genuinely lived is decided by
 * two real eyes rather than by surviving the search, so the classical shapes fall
 * out of the rules instead of being tabulated: a two-point eye dies, a straight
 * four lives, and a three-point eye comes back `unsettled` because whoever takes
 * the middle point wins it.
 *
 * `unknown` is a real answer here and not a failure. A group still open to the
 * board, a wall thin enough that capturing it is part of the problem, or a shape
 * deeper than the allowance all come back unread rather than guessed at.
 */
export function readGroup(
  position: Position,
  chain: Group,
  space: EyeSpace,
  shared: ReadBudget = readBudget(position),
): LifeStatus {
  if (!space.confined) return 'unknown';

  const search = newSearch(position, space, shared);
  if (!wallsAreSolid(position, chain.color, search.mask)) return 'unknown';

  const anchor = chain.stones[0];
  const depth = searchDepth(space);

  const attacked = survives(position, anchor, chain.color, search, opponent(chain.color), depth);
  if (attacked === true) return 'alive';

  const defended = survives(position, anchor, chain.color, search, chain.color, depth);
  if (defended === false) return 'dead';

  if (attacked === false && defended === true) return 'unsettled';
  return 'unknown';
}

function newSearch(position: Position, space: EyeSpace, shared: ReadBudget): Search {
  const mask = new Uint8Array(position.area);
  for (const v of space.empties) mask[v] = 1;
  for (const v of space.enemies) mask[v] = 1;
  return { mask, limit: space.empties.length, memo: new Map(), own: NODE_BUDGET, shared };
}

function searchDepth(space: EyeSpace): number {
  return 2 * space.empties.length + 6;
}

/**
 * Can the group at `anchor` still be alive, with `toMove` to play?
 *
 * Play is restricted to `mask` — the group's own eye space — which is what makes
 * the search small enough to be exhaustive. The bounds are honest: running out
 * of depth, or finding a capture the mask cannot see, returns `null` rather than
 * a verdict.
 */
function survives(
  position: Position,
  anchor: Vertex,
  color: Color,
  search: Search,
  toMove: Color,
  depth: number,
): Verdict {
  // The allowance is checked before anything else, including examining the board.
  // Walking a node is not cheap — reading the group and playing out each candidate
  // both copy the board — so an exhausted search has to stop here rather than keep
  // expanding a tree it will only answer `null` for.
  if (exhausted(search)) return null;

  const chain = position.group(anchor);
  if (!chain || chain.color !== color) return false; // captured

  // Ask the table before doing any work: a hit already accounts for everything
  // below, including the life proof.
  const key = `${hashKey(position.hash)}:${toMove}`;
  const cached = search.memo.get(key);
  if (cached && (cached.verdict !== null || cached.depth >= depth)) return cached.verdict;

  search.own--;
  search.shared.nodes--;

  const regions = countLibertyRegions(position, chain, search.limit);
  if (regions === null) return null; // broke out of the space being read
  if (regions >= 2 && hasTwoEyesOfItsOwn(position, chain, search.limit)) return true;

  if (depth <= 0) return null;

  const mask = search.mask;
  const moves: Vertex[] = [];
  for (let v = 0; v < mask.length; v++) {
    if (!mask[v] || position.at(v) !== EMPTY) continue;
    if (position.isLegal({ type: 'play', vertex: v }, toMove)) moves.push(v);
  }

  let verdict: Verdict;
  if (toMove === color) {
    // The defender needs one move that works. Passing is included: with a big
    // enough eye space, adding a stone only helps the attacker.
    verdict = false;
    for (const move of [...moves, null]) {
      if (exhausted(search)) return null;
      const next = position.play(move === null ? { type: 'pass' } : { type: 'play', vertex: move }, color);
      const result = survives(next.position, anchor, color, search, opponent(color), depth - 1);
      if (result === true) {
        verdict = true;
        break;
      }
      if (result === null) verdict = null;
    }
  } else {
    // The attacker needs one move that kills. Passing cannot help, and having
    // nothing legal left to try means the group has lived.
    verdict = true;
    for (const move of moves) {
      if (exhausted(search)) return null;
      const next = position.play({ type: 'play', vertex: move }, toMove);
      const result = survives(next.position, anchor, color, search, color, depth - 1);
      if (result === false) {
        verdict = false;
        break;
      }
      if (result === null) verdict = null;
    }
  }

  if (!cached || cached.verdict === null) search.memo.set(key, { verdict, depth });
  return verdict;
}

function exhausted(search: Search): boolean {
  return search.own <= 0 || search.shared.nodes <= 0;
}

/**
 * How many separate empty regions the group's liberties fall into — its eye
 * count, before asking whether those eyes are real.
 *
 * Returns null if the group can now reach more empty space than the read started
 * with, which means a surrounding wall was captured and the mask no longer
 * describes the problem.
 */
function countLibertyRegions(position: Position, chain: Group, limit: number): number | null {
  const { nbr, off } = position.geo;
  const seen = new Set<Vertex>();
  let regions = 0;

  for (const start of chain.liberties) {
    if (seen.has(start)) continue;
    regions++;
    const queue = [start];
    seen.add(start);
    for (let i = 0; i < queue.length; i++) {
      if (seen.size > limit) return null;
      const cur = queue[i];
      for (let k = off[cur]; k < off[cur + 1]; k++) {
        const n = nbr[k];
        if (position.stones[n] !== EMPTY || seen.has(n)) continue;
        seen.add(n);
        queue.push(n);
      }
    }
  }
  return regions;
}

/**
 * Benson's condition applied to a single chain: two separate regions that this
 * chain alone borders, each of whose empty points all touch it.
 *
 * This is the test `survives` uses at every node, and it exists because the full
 * algorithm is far too expensive to run there — on a crowded 19x19 board it walks
 * every chain on the board, tens of thousands of times over a single read.
 *
 * It is *sufficient but not complete*: it drops the case of two chains that are
 * unconditionally alive only by supporting each other. That costs the search a
 * shortcut and never an answer, because a group with two real eyes is recognised
 * anyway when the attacker runs out of legal moves to try. So nothing is
 * misreported as dead — the honest direction to fail in.
 */
function hasTwoEyesOfItsOwn(position: Position, chain: Group, limit: number): boolean {
  const { nbr, off } = position.geo;
  const color = chain.color;
  const own = new Set(chain.stones);
  const seen = new Set<Vertex>();
  const cap = limit * 2 + 8;
  let eyes = 0;

  for (const start of chain.liberties) {
    if (seen.has(start)) continue;

    // The whole region has to be walked even once it is known to fail, or a later
    // liberty in the same region would be counted as a second eye.
    const region: Vertex[] = [start];
    seen.add(start);
    let vital = true;

    for (let i = 0; i < region.length; i++) {
      if (region.length > cap) return false; // bigger than any eye; claim nothing
      const cur = region[i];
      let touchesOwn = false;

      for (let k = off[cur]; k < off[cur + 1]; k++) {
        const n = nbr[k];
        if (position.stones[n] === color) {
          if (own.has(n)) touchesOwn = true;
          else vital = false; // a different friendly chain also borders this
        } else if (!seen.has(n)) {
          seen.add(n);
          region.push(n);
        }
      }

      // Every empty point of a vital region touches the chain itself.
      if (position.stones[cur] === EMPTY && !touchesOwn) vital = false;
    }

    if (vital && ++eyes >= 2) return true;
  }
  return false;
}

/**
 * Is the wall around this eye space too solid to be captured by play inside it?
 *
 * The search is confined to the mask, so it cannot see the defender escaping by
 * capturing one of the surrounding chains — and a read that misses an escape would
 * call a living group dead. Rather than test for that at every node, which means
 * re-walking every neighbouring wall (and walls are the biggest chains on the
 * board), it is settled once here.
 *
 * The argument: play inside the mask never touches a wall's liberties outside it,
 * so a wall with two or more outside liberties can never be reduced to one, and no
 * escape-by-capture can arise however the search goes. Enemy stones *trapped* in
 * the eye space are a different thing — capturing those is part of the problem, and
 * the search handles them. Anything in between is a capturing race rather than a
 * life-and-death problem, and the reader declines it instead of guessing.
 */
function wallsAreSolid(position: Position, color: Color, mask: Uint8Array): boolean {
  const { nbr, off } = position.geo;
  const foe = opponent(color);
  const seen = new Uint8Array(position.area);

  for (let v = 0; v < mask.length; v++) {
    if (!mask[v]) continue;
    for (let k = off[v]; k < off[v + 1]; k++) {
      const n = nbr[k];
      if (position.stones[n] !== foe || seen[n]) continue;
      const enemy = position.group(n);
      if (!enemy) continue;
      for (const stone of enemy.stones) seen[stone] = 1;

      let outside = 0;
      for (const liberty of enemy.liberties) {
        if (!mask[liberty]) outside++;
      }
      // Trapped inside (0 outside) is fine; a solid wall (2 or more) is fine.
      if (outside === 1) return false;
    }
  }
  return true;
}

// --- The answer -----------------------------------------------------------

export interface LifeOptions {
  /** Largest eye space to search. Raising it costs time and settles more groups. */
  readonly maxEyeSpace?: number;
  /**
   * Node allowance shared across every group read from this position. Pass one
   * budget to two `lifeAndDeath` calls to bound the cost of reading both colours.
   */
  readonly budget?: ReadBudget;
}

/** The full reading for the group at `vertex`, or null if the point is empty. */
export function groupLife(
  position: Position,
  vertex: Vertex,
  options: LifeOptions = {},
): GroupLife | null {
  const chain = position.group(vertex);
  if (!chain) return null;
  const budget = options.budget ?? readBudget(position);
  return analyse(position, chain, passAliveChains(position, chain.color), options, budget);
}

/**
 * Every stone the reader is confident is dead, both colours.
 *
 * This is what scoring needs: at the end of a game the board is settled enough
 * that most groups are either provably alive or plainly dead, and the ones the
 * reader will not commit on are left alive — the safe direction, and the same
 * answer the engine gave before it could read at all.
 *
 * It is a starting point for the players to correct, never the last word. The
 * reader declines more than it answers, and two people who disagree with it are
 * right by definition: the rules settle games by agreement, not by analysis.
 */
export function deadStones(position: Position, options: LifeOptions = {}): Vertex[] {
  const budget = options.budget ?? readBudget(position);
  const dead: Vertex[] = [];
  for (const color of [1, 2] as const) {
    for (const life of lifeAndDeath(position, color, { ...options, budget })) {
      if (life.status === 'dead') dead.push(...life.stones);
    }
  }
  return dead;
}

/** A reading for every group of `color`. Shares one pass-alive proof across them. */
export function lifeAndDeath(
  position: Position,
  color: Color,
  options: LifeOptions = {},
): GroupLife[] {
  const proven = passAliveChains(position, color);
  const budget = options.budget ?? readBudget(position);
  return chains(position, color).map((chain) => analyse(position, chain, proven, options, budget));
}

function analyse(
  position: Position,
  chain: Group,
  proven: readonly Group[],
  options: LifeOptions,
  budget: ReadBudget,
): GroupLife {
  const anchor = chain.stones[0];
  const common = {
    color: chain.color,
    stones: chain.stones,
    eyes: trueEyes(position, chain),
    falseEyes: falseEyes(position, chain),
  };

  if (proven.some((alive) => alive.stones.includes(anchor))) {
    return { ...common, status: 'alive', certainty: 'proven', eyeSpace: chain.liberties };
  }

  const space = eyeSpace(position, chain, options.maxEyeSpace ?? MAX_EYE_SPACE);
  const status = readGroup(position, chain, space, budget);
  const life: GroupLife = {
    ...common,
    status,
    certainty: status === 'unknown' ? 'unread' : 'read',
    eyeSpace: space.empties,
  };

  if (status !== 'unsettled') return life;
  const vitalPoint = findVitalPoint(position, chain, space, budget);
  return vitalPoint === undefined ? life : { ...life, vitalPoint };
}

/**
 * The one move an unsettled group turns on. Searched from the attacker's side:
 * the point that kills is, in these shapes, the same point that lives.
 */
function findVitalPoint(
  position: Position,
  chain: Group,
  space: EyeSpace,
  shared: ReadBudget,
): Vertex | undefined {
  const search = newSearch(position, space, shared);
  const anchor = chain.stones[0];
  const foe = opponent(chain.color);
  const depth = searchDepth(space);

  for (const move of space.empties) {
    if (!position.isLegal({ type: 'play', vertex: move }, foe)) continue;
    const next = position.play({ type: 'play', vertex: move }, foe);
    if (survives(next.position, anchor, chain.color, search, chain.color, depth) === false) {
      return move;
    }
  }
  return undefined;
}
